import { useState, useRef, useEffect, useCallback, memo } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { marked } from "marked";
import { FONT } from "../theme/tokens";
import { v, api } from "../utils/helpers";
import { useAppTheme } from "../theme/ThemeContext";
import useDashboardStore from "../store/useDashboardStore";

// Heuristik: erkennt Anfragen die eine Änderung implizieren
const WRITE_INTENT_RE = /(\bfix(e|en)?\b|\baender|\bändere|\bändern|\bimplementier|\berstell|\bbaue|\bdeploy|\bbehebe|\brepariere|\bkorrigiere|\bf(uü)ge .*hinzu|\bersetze|\boptimiere|\brefactor)/i;
const detectWriteIntent = (s) => typeof s === "string" && WRITE_INTENT_RE.test(s);

// Die Kennungen der Agenten; die Beschriftung dazu steht im Katalog unter chat.agent.*
const AGENT_KEYS = ["coordinator", "developer", "validator", "qa", "chat"];
const STATUS_DOT = {
  running: { color: "var(--accent)" },
  pass:    { color: "var(--success)" },
  done:    { color: "var(--success)" },
  warn:    { color: "var(--warning)" },
  fail:    { color: "var(--danger)" },
};

/* ── marked config ───────────────────────────────────────── */
marked.use({
  breaks: true,
  gfm: true,
  renderer: {
    code({ text, lang }) {
      // Detect SVG content in any code block (svg, xml, or untagged)
      if (text && text.includes('<svg') && text.includes('</svg>')) {
        const encoded = encodeURIComponent(text);
        return `<div class="cp-svg-wrap" data-svg="${encoded}">${text}<button class="cp-dl-btn" data-action="download-svg">${i18n.t("builder:chat.eingabe.svgLaden")}</button></div>`;
      }
      return false; // fall through to default renderer
    }
  }
});

/* ── localStorage helpers ────────────────────────────────── */
const HISTORY_KEY = "cpi-chat-history";
const loadHistory = () => {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]"); } catch { return []; }
};
const saveHistory = (convs) => {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(convs)); } catch {}
};
const genId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
// Der Titel wird in den Verlauf geschrieben und ist damit gespeicherter Inhalt.
// Er bleibt woertlich, sonst haengt die Sprache eines alten Chats davon ab,
// welche Sprache beim Speichern eingestellt war.
const titleFromMessages = (msgs) => {
  const first = msgs.find(m => m.role === "user");
  if (!first) return "Neuer Chat";
  const text = typeof first.content === "string" ? first.content : "Neuer Chat";
  return text.length > 50 ? text.slice(0, 50) + "..." : text;
};

/* ── keyframes + styles (injected once) ──────────────────── */
const styleId = "chat-panel-styles";
const ensureStyles = () => {
  if (document.getElementById(styleId)) return;
  const s = document.createElement("style");
  s.id = styleId;
  s.textContent = `
    @keyframes dotPulse{0%,80%,100%{opacity:.25;transform:scale(.8)}40%{opacity:1;transform:scale(1)}}
    @keyframes chatSlideUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
    @keyframes chatSlideDown{from{opacity:1;transform:translateY(0)}to{opacity:0;transform:translateY(16px)}}
    .cp-md{font-size:13px;line-height:1.65;word-break:break-word}
    .cp-md h1,.cp-md h2,.cp-md h3{font-weight:700;margin:8px 0 4px;line-height:1.3}
    .cp-md h1{font-size:16px}.cp-md h2{font-size:15px}.cp-md h3{font-size:14px}
    .cp-md p{margin:4px 0}
    .cp-md ul,.cp-md ol{margin:4px 0 4px 18px;padding:0}
    .cp-md li{margin:2px 0}
    .cp-md code{font-family:inherit;background:rgba(75,127,250,.1);padding:1px 5px;border-radius:4px;font-size:12px}
    .cp-md pre{background:var(--surface-2);border:1px solid var(--border);border-radius:8px;padding:10px 12px;overflow-x:auto;margin:6px 0;position:relative}
    .cp-md pre code{background:none;padding:0;font-family:inherit;font-size:12px;line-height:1.5}
    .cp-md table{width:100%;border-collapse:collapse;margin:6px 0;font-size:12px}
    .cp-md th{text-align:left;padding:5px 8px;border-bottom:2px solid var(--border);font-weight:600;color:var(--text-2)}
    .cp-md td{padding:4px 8px;border-bottom:1px solid var(--border)}
    .cp-md blockquote{border-left:3px solid var(--accent);margin:6px 0;padding:4px 12px;color:var(--text-2);background:rgba(75,127,250,.04);border-radius:0 6px 6px 0}
    .cp-md a{color:var(--accent);text-decoration:none}
    .cp-md img{max-width:100%;border-radius:8px;margin:4px 0}
    .cp-md hr{border:none;border-top:1px solid var(--border);margin:8px 0}
    .cp-md ::selection,.cp-md *::selection{background:rgba(75,127,250,.35);color:inherit}
    .cp-user-msg ::selection,.cp-user-msg *::selection{background:rgba(75,127,250,.4);color:inherit}
    .cp-svg-wrap{position:relative;margin:6px 0;background:var(--surface-2);border:1px solid var(--border);border-radius:8px;padding:12px;overflow:auto}
    .cp-svg-wrap svg{max-width:100%;height:auto}
    .cp-dl-btn{position:absolute;top:6px;right:6px;font-size:10px;padding:3px 8px;border-radius:5px;border:1px solid var(--border);background:var(--surface-1);color:var(--text-2);cursor:pointer;font-family:inherit;transition:all .15s}
    .cp-dl-btn:hover{background:var(--accent);color:#fff;border-color:var(--accent)}
    .cp-copy-btn{position:absolute;top:6px;right:6px;font-size:10px;padding:3px 8px;border-radius:5px;border:1px solid var(--border);background:var(--surface-1);color:var(--text-2);cursor:pointer;font-family:inherit;transition:all .15s}
    .cp-copy-btn:hover{background:var(--accent);color:#fff;border-color:var(--accent)}
    .cp-quote-btn{position:fixed;z-index:9999;font-size:11px;padding:4px 10px;border-radius:6px;border:1px solid var(--border);background:var(--surface-0);color:var(--text-1);cursor:pointer;font-family:inherit;box-shadow:0 4px 16px rgba(0,0,0,.3);transition:all .12s}
    .cp-quote-btn:hover{background:var(--accent);color:#fff}
    .cp-img-preview{max-width:100%;max-height:120px;border-radius:8px;object-fit:cover;border:1px solid var(--border)}
    .cp-resize-handle{position:absolute;top:0;left:0;width:16px;height:16px;cursor:nw-resize;z-index:10;display:flex;align-items:center;justify-content:center}
    .cp-resize-handle svg{opacity:.3;transition:opacity .15s}
    .cp-resize-handle:hover svg{opacity:.8}
    .cp-hist-item{padding:10px 12px;cursor:pointer;border-bottom:1px solid var(--border);transition:background .12s}
    .cp-hist-item:hover{background:var(--surface-2)}
    .cp-hist-item.active{background:rgba(75,127,250,.1);border-left:3px solid var(--accent)}
  `;
  document.head.appendChild(s);
};

/* SVG rendering is handled by the custom marked renderer above */

/* ── pulsing dots ────────────────────────────────────────── */
const Dots = () => (
  <span style={{ display: "inline-flex", gap: 4, padding: "8px 0" }}>
    {[0, 1, 2].map(i => <span key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: v("text-3"), animation: `dotPulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />)}
  </span>
);

/* ── capability labels ───────────────────────────────────── */
// Die Kennungen der Faehigkeiten; die Beschriftung steht im Katalog unter chat.faehigkeit.*
const CAP_KEYS = ["read", "monitor", "write", "deploy", "test", "tpm"];

/* ── main component ──────────────────────────────────────── */
const ChatPanel = memo(({ open, onClose, spaces, activeInst }) => {
  const { t } = useTranslation(["builder", "gemeinsam"]);
  const gebiet = i18n.language === "en" ? "en-GB" : "de-DE";
  const { theme } = useAppTheme();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedMode, setSelectedMode] = useState("general");
  const [visible, setVisible] = useState(false);
  const [animClass, setAnimClass] = useState("");
  const [aiInstances, setAiInstances] = useState([]);
  const [attachedImages, setAttachedImages] = useState([]);
  const [quoteBtn, setQuoteBtn] = useState(null);
  const [panelSize, setPanelSize] = useState({ w: 480, h: 620 });
  /* chat history */
  const [conversations, setConversations] = useState(() => loadHistory());
  const [activeConvId, setActiveConvId] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  /* Multi-Agent-Pipeline state */
  const [agentStatus, setAgentStatus] = useState(null); // {agents:{coordinator|developer|validator|qa: {status, step}}, mode}
  const [pipelineSummary, setPipelineSummary] = useState(null); // bei "deploy_gate" gesetzt
  const currentFlowContext = useDashboardStore(s => s.currentFlowContext);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const fileRef = useRef(null);
  const resizing = useRef(false);
  const resizeStart = useRef({ x: 0, y: 0, w: 0, h: 0 });

  useEffect(() => { ensureStyles(); }, []);

  /* load AI instances */
  useEffect(() => {
    if (open) api("/api/ai-enabled-instances").then(setAiInstances).catch(() => setAiInstances([]));
  }, [open]);

  /* auto-select active instance when navigating */
  useEffect(() => {
    if (!activeInst?.id || !aiInstances.length) return;
    const match = aiInstances.find(i => i.id === activeInst.id);
    if (match) setSelectedMode(match.id);
  }, [activeInst?.id, aiInstances]);

  /* Auto-select Instanz wenn ein iFlow im Store registriert ist (Multi-Agent-Kontext) */
  useEffect(() => {
    if (!currentFlowContext?.instanceId || !aiInstances.length) return;
    const match = aiInstances.find(i => i.id === currentFlowContext.instanceId);
    if (match && selectedMode !== match.id) setSelectedMode(match.id);
  }, [currentFlowContext?.instanceId, aiInstances]); // eslint-disable-line react-hooks/exhaustive-deps

  /* open/close animation */
  useEffect(() => {
    if (open) {
      setVisible(true);
      setAnimClass("opening");
      requestAnimationFrame(() => inputRef.current?.focus());
    } else if (visible) {
      setAnimClass("closing");
      const t = setTimeout(() => { setVisible(false); setAnimClass(""); }, 220);
      return () => clearTimeout(t);
    }
  }, [open, visible]);

  /* auto-scroll */
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  /* auto-save conversation when messages change */
  useEffect(() => {
    if (!messages.length) return;
    setConversations(prev => {
      let updated;
      if (activeConvId) {
        updated = prev.map(c => c.id === activeConvId
          ? { ...c, messages, title: titleFromMessages(messages), updatedAt: Date.now(), mode: selectedMode }
          : c
        );
      } else {
        const newId = genId();
        setActiveConvId(newId);
        updated = [{ id: newId, title: titleFromMessages(messages), messages, mode: selectedMode, createdAt: Date.now(), updatedAt: Date.now() }, ...prev];
      }
      saveHistory(updated);
      return updated;
    });
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  /* click handler for SVG download + code copy */
  useEffect(() => {
    const handler = (e) => {
      const btn = e.target.closest("[data-action]");
      if (!btn) return;
      if (btn.dataset.action === "download-svg") {
        const wrap = btn.closest(".cp-svg-wrap");
        const svg = decodeURIComponent(wrap.dataset.svg);
        const blob = new Blob([svg], { type: "image/svg+xml" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "chart.svg";
        a.click();
        URL.revokeObjectURL(a.href);
      }
      if (btn.dataset.action === "copy-code") {
        const pre = btn.closest("pre");
        const code = pre?.querySelector("code")?.textContent || "";
        navigator.clipboard.writeText(code);
        // i18n.t statt t: der Zuhoerer wird einmal beim Einhaengen erzeugt.
        btn.textContent = i18n.t("builder:chat.eingabe.kopiert");
        setTimeout(() => { btn.textContent = i18n.t("builder:chat.eingabe.kopieren"); }, 1500);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  /* text selection → quote button */
  useEffect(() => {
    const handler = () => {
      const sel = window.getSelection();
      const text = sel?.toString()?.trim();
      if (!text || text.length < 3) { setQuoteBtn(null); return; }
      const node = sel.anchorNode?.parentElement;
      if (!panelRef.current?.contains(node)) { setQuoteBtn(null); return; }
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      setQuoteBtn({ x: rect.left + rect.width / 2, y: rect.top - 36, text });
    };
    document.addEventListener("mouseup", handler);
    return () => document.removeEventListener("mouseup", handler);
  }, []);

  const quoteText = (text) => {
    setInput(prev => (prev ? prev + "\n" : "") + `> ${text}\n`);
    setQuoteBtn(null);
    window.getSelection()?.removeAllRanges();
    inputRef.current?.focus();
  };

  /* resize */
  const startResize = useCallback((e) => {
    e.preventDefault();
    resizing.current = true;
    resizeStart.current = { x: e.clientX, y: e.clientY, w: panelSize.w, h: panelSize.h };
    const move = (ev) => {
      if (!resizing.current) return;
      const dw = resizeStart.current.x - ev.clientX;
      const dh = resizeStart.current.y - ev.clientY;
      setPanelSize({
        w: Math.max(380, Math.min(900, resizeStart.current.w + dw)),
        h: Math.max(400, Math.min(window.innerHeight - 120, resizeStart.current.h + dh)),
      });
    };
    const up = () => { resizing.current = false; document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", up); };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  }, [panelSize]);

  /* image upload */
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      if (!file.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        setAttachedImages(prev => [...prev, { name: file.name, base64, mime: file.type }]);
      };
      reader.readAsDataURL(file);
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeImage = (idx) => setAttachedImages(prev => prev.filter((_, i) => i !== idx));

  /* paste image from clipboard */
  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(",")[1];
          setAttachedImages(prev => [...prev, { name: "clipboard.png", base64, mime: file.type }]);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  /* conversation management */
  const startNewChat = () => {
    setMessages([]);
    setAttachedImages([]);
    setActiveConvId(null);
    setShowHistory(false);
    inputRef.current?.focus();
  };

  const loadConversation = (conv) => {
    setMessages(conv.messages || []);
    setActiveConvId(conv.id);
    setSelectedMode(conv.mode || "general");
    setShowHistory(false);
  };

  const deleteConversation = (id, e) => {
    e.stopPropagation();
    setConversations(prev => {
      const updated = prev.filter(c => c.id !== id);
      saveHistory(updated);
      return updated;
    });
    if (activeConvId === id) {
      setMessages([]);
      setActiveConvId(null);
    }
  };

  const selectedInst = aiInstances.find(i => i.id === selectedMode);
  const activeCaps = selectedInst?.aiCapabilities?.features || {};

  // Multi-Agent-Modus möglich, wenn:
  //  - eine konkrete Instanz gewählt ist (kein "general"/"all")
  //  - die Instanz aiCapabilities.enabled hat
  //  - ein iFlow-Kontext im Store gesetzt ist (URL passt zur Instanz)
  const flowCtxMatchesInstance = currentFlowContext?.instanceId === selectedMode;
  const canUseAgentPipeline = !!(selectedInst?.aiCapabilities?.enabled && flowCtxMatchesInstance && currentFlowContext?.flowId);

  /* send */
  const send = useCallback(async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if ((!text && !attachedImages.length) || loading) return;

    const userMsg = { role: "user", content: text, images: attachedImages.length ? [...attachedImages] : undefined };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setAttachedImages([]);
    setLoading(true);
    setPipelineSummary(null);

    // Heuristik: Multi-Agent-Pipeline nur, wenn Write-Intent + Capability + Flow-Kontext
    const wantsWrite = detectWriteIntent(text);
    const useAgentMode = canUseAgentPipeline && wantsWrite && !!activeCaps?.write;
    const useStream = useAgentMode || canUseAgentPipeline; // bei Flow-Kontext immer SSE-Pfad nutzen
    if (useAgentMode) setAgentStatus({ agents: {}, mode: "agent", events: [] });
    else setAgentStatus(null);

    const apiMessages = next.map(m => {
      if (m.images?.length) {
        const content = [];
        m.images.forEach(img => content.push({ type: "image", source: { type: "base64", media_type: img.mime, data: img.base64 } }));
        if (m.content) content.push({ type: "text", text: m.content });
        return { role: m.role, content };
      }
      return { role: m.role, content: m.content };
    });

    try {
      // ── Pfad A: SSE-Stream mit Tool-Loop / Multi-Agent ───────────────────
      if (useStream) {
        const body = {
          messages: apiMessages,
          instanceId: selectedMode,
          flowContext: currentFlowContext || null,
          mode: useAgentMode ? "agent" : undefined,
        };
        const token = localStorage.getItem("cpi_token");
        const resp = await fetch("/api/iflow-chat/stream", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
          body: JSON.stringify(body),
        });
        if (!resp.ok) {
          const err = await resp.text().catch(() => "");
          throw new Error(`HTTP ${resp.status}: ${err.slice(0, 200)}`);
        }
        const reader = resp.body.getReader();
        const dec = new TextDecoder();
        let buf = "", assembledText = "", lastSummary = null;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const chunks = buf.split("\n\n");
          buf = chunks.pop() || "";
          for (const chunk of chunks) {
            for (const line of chunk.split("\n")) {
              if (!line.startsWith("data: ")) continue;
              const payload = line.slice(6).trim();
              if (!payload) continue;
              try {
                const j = JSON.parse(payload);
                if (j.type === "session") {
                  setAgentStatus(prev => ({ agents: prev?.agents || {}, mode: j.mode, events: prev?.events || [] }));
                } else if (j.type === "agent") {
                  setAgentStatus(prev => {
                    const cur = prev || { agents: {}, mode: null, events: [] };
                    return { ...cur, agents: { ...cur.agents, [j.agent || "coordinator"]: { status: j.status, step: j.step } } };
                  });
                } else if (j.type === "tool_use") {
                  setAgentStatus(prev => {
                    const cur = prev || { agents: {}, mode: null, events: [] };
                    return { ...cur, agents: { ...cur.agents, [j.agent || "developer"]: { status: "running", step: `Tool: ${j.tool}` } } };
                  });
                } else if (j.type === "tool_result") {
                  setAgentStatus(prev => {
                    const cur = prev || { agents: {}, mode: null, events: [] };
                    const a = cur.agents?.[j.agent] || {};
                    return { ...cur, agents: { ...cur.agents, [j.agent]: { ...a, status: j.ok ? "running" : "warn", step: `${j.tool} ${j.ok ? i18n.t("builder:chat.pipeline.werkzeugOk") : i18n.t("builder:chat.pipeline.werkzeugFehler")}` } } };
                  });
                } else if (j.type === "assistant_text") {
                  assembledText = j.text || "";
                } else if (j.type === "text") {
                  assembledText = j.text || "";
                } else if (j.type === "deploy_gate") {
                  lastSummary = j.summary;
                  setPipelineSummary(j.summary);
                } else if (j.type === "pipeline_done") {
                  if (j.summary) lastSummary = j.summary;
                  if (j.summary?.devText) assembledText = j.summary.devText;
                } else if (j.type === "error") {
                  assembledText = (assembledText ? assembledText + "\n\n" : "") + i18n.t("builder:chat.meldung.fehler", { meldung: j.error });
                }
              } catch {}
            }
          }
        }
        setMessages(prev => [...prev, { role: "assistant", content: assembledText || i18n.t("builder:chat.meldung.keineAntwort"), agentSummary: lastSummary }]);
      } else {
        // ── Pfad B: klassischer /api/chat (allgemein, multi-instance, etc.) ──
        const payload = { messages: apiMessages, iflowContext: {} };
        if (selectedMode === "all") payload.iflowContext.allInstances = aiInstances;
        else if (selectedMode !== "general") payload.iflowContext.instanceId = selectedMode;
        if (currentFlowContext?.flowId && currentFlowContext.instanceId === selectedMode) {
          payload.iflowContext.flowId = currentFlowContext.flowId;
          payload.iflowContext.flowName = currentFlowContext.flowName;
        }
        const res = await api("/api/chat", { method: "POST", body: JSON.stringify(payload) });
        setMessages(prev => [...prev, { role: "assistant", content: res.response || i18n.t("builder:chat.meldung.keineAntwort") }]);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: "assistant", content: i18n.t("builder:chat.meldung.fehler", { meldung: err.message || i18n.t("builder:chat.meldung.unbekannt") }) }]);
    } finally {
      setLoading(false);
    }
  }, [input, messages, loading, selectedMode, aiInstances, attachedImages, canUseAgentPipeline, currentFlowContext, activeCaps]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (!visible) return null;

  const isOpening = animClass === "opening";
  const modeLabel = selectedMode === "general"
    ? t("chat.kontext.modusAllgemein")
    : selectedMode === "all"
      ? t("chat.kontext.modusAlle", { count: aiInstances.length })
      : selectedInst
        ? t("chat.kontext.modusInstanz", { tools: CAP_KEYS.filter(k => activeCaps[k]).map(k => t("chat.faehigkeit." + k)).join(", ") || t("chat.kontext.keineTools") })
        : "";

  const formatDate = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const diffDays = Math.floor((now - d) / 86400000);
    const zeit = d.toLocaleTimeString(gebiet, { hour: "2-digit", minute: "2-digit" });
    if (diffDays === 0) return t("chat.verlauf.heute", { zeit });
    if (diffDays === 1) return t("chat.verlauf.gestern", { zeit });
    return t("chat.verlauf.datum", { datum: d.toLocaleDateString(gebiet, { day: "2-digit", month: "2-digit" }), zeit });
  };

  return (
    <>
      {/* Quote button */}
      {quoteBtn && (
        <button className="cp-quote-btn" style={{ left: quoteBtn.x - 40, top: quoteBtn.y }}
          onClick={() => quoteText(quoteBtn.text)}>
          {t("chat.eingabe.zitieren")}
        </button>
      )}

      <div ref={panelRef} style={{
        position: "fixed", bottom: 88, right: 24, width: panelSize.w, height: panelSize.h,
        maxHeight: "calc(100vh - 120px)", borderRadius: 16, background: v("surface-0"),
        border: `1px solid ${v("border")}`, boxShadow: "0 8px 40px rgba(0,0,0,.4)",
        display: "flex", flexDirection: "column", fontFamily: FONT, zIndex: 1200, overflow: "hidden",
        animation: isOpening ? "chatSlideUp .22s ease forwards" : "chatSlideDown .22s ease forwards",
      }}>
        {/* Resize handle top-left */}
        <div className="cp-resize-handle" onMouseDown={startResize}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke={v("text-3")} strokeWidth="1.5">
            <line x1="0" y1="10" x2="10" y2="0" /><line x1="0" y1="6" x2="6" y2="0" /><line x1="0" y1="2" x2="2" y2="0" />
          </svg>
        </div>

        {/* ── Header ──────────────────────────────────────── */}
        <div style={{ borderBottom: `1px solid ${v("border")}`, flexShrink: 0 }}>
          <div style={{ padding: "10px 16px 6px", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: v("text-1") }}>{t("chat.kopf.titel")}</span>
            <div style={{ flex: 1 }} />
            <button onClick={() => setShowHistory(h => !h)} title={t("chat.kopf.verlaufTitel")}
              style={{ background: showHistory ? v("surface-2") : "none", border: "none", color: showHistory ? "var(--accent)" : v("text-3"), cursor: "pointer", padding: 4, display: "flex", borderRadius: 6 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
            </button>
            <button onClick={startNewChat} title={t("chat.kopf.neu")}
              style={{ background: "none", border: "none", color: v("text-3"), cursor: "pointer", padding: 4, display: "flex" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            </button>
            <button onClick={onClose} aria-label={t("chat.kopf.schliessen")}
              style={{ background: "none", border: "none", color: v("text-2"), cursor: "pointer", padding: 4, display: "flex" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* ── Instance context bar ──────────────────────── */}
          <div style={{ padding: "0 16px 8px", display: "flex", alignItems: "center", gap: 8 }}>
            {selectedInst ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1, minWidth: 0 }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: v("success"), flexShrink: 0 }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: v("text-1"), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selectedInst.name}
                </span>
                <span style={{ fontSize: 10, color: v("text-3"), flexShrink: 0 }}>
                  {selectedInst.spaceName ? `(${selectedInst.spaceName})` : ""}
                </span>
              </div>
            ) : (
              <span style={{ fontSize: 12, color: v("text-3"), flex: 1 }}>
                {selectedMode === "all" ? t("gemeinsam:einheit.instanz", { count: aiInstances.length }) : t("chat.kontext.keineInstanz")}
              </span>
            )}
            <select value={selectedMode} onChange={e => setSelectedMode(e.target.value)}
              style={{ fontFamily: FONT, fontSize: 10, padding: "3px 6px", borderRadius: 6, border: `1px solid ${v("border")}`, background: v("surface-1"), color: v("text-2"), maxWidth: 130, outline: "none", cursor: "pointer", flexShrink: 0 }}>
              <option value="general">{t("chat.kontext.allgemein")}</option>
              {aiInstances.length > 1 && <option value="all">{t("chat.kontext.alleInstanzen")}</option>}
              {aiInstances.map(inst => <option key={inst.id} value={inst.id}>{inst.name}</option>)}
            </select>
          </div>

          {/* ── Mode hint + Flow-Kontext ─────────────────── */}
          <div style={{ padding: "4px 16px 6px", fontSize: 10, color: v("text-3"), borderTop: `1px solid ${v("border-subtle")}`, letterSpacing: ".02em" }}>
            {modeLabel}
            {flowCtxMatchesInstance && currentFlowContext?.flowId && (
              <span> · iFlow <span style={{ color: canUseAgentPipeline ? v("accent") : v("text-2") }}>{currentFlowContext.flowName || currentFlowContext.flowId}</span>{activeCaps?.write ? t("chat.kontext.multiAgent") : ""}</span>
            )}
          </div>
        </div>

        {/* ── History panel (overlay) ─────────────────────── */}
        {showHistory && (
          <div style={{
            position: "absolute", top: 82, left: 0, right: 0, bottom: 0, zIndex: 5,
            background: v("surface-0"), display: "flex", flexDirection: "column",
          }}>
            <div style={{ padding: "10px 16px", fontSize: 13, fontWeight: 600, color: v("text-1"), borderBottom: `1px solid ${v("border")}`, display: "flex", alignItems: "center" }}>
              {t("chat.verlauf.titel")}
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 11, color: v("text-3"), fontWeight: 400 }}>{conversations.length}</span>
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {conversations.length === 0 && (
                <div style={{ padding: 24, textAlign: "center", color: v("text-3"), fontSize: 12 }}>
                  {t("chat.verlauf.leer")}
                </div>
              )}
              {conversations.map(conv => (
                <div key={conv.id} className={`cp-hist-item${conv.id === activeConvId ? " active" : ""}`}
                  onClick={() => loadConversation(conv)}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: v("text-1"), marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {conv.title}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 10, color: v("text-3") }}>{formatDate(conv.updatedAt || conv.createdAt)}</span>
                    <span style={{ fontSize: 10, color: v("text-3") }}>{t("chat.verlauf.nachrichten", { count: conv.messages?.length || 0 })}</span>
                    <span style={{ flex: 1 }} />
                    <button onClick={(e) => deleteConversation(conv.id, e)} title={t("chat.verlauf.loeschen")}
                      style={{ background: "none", border: "none", color: v("text-3"), cursor: "pointer", padding: "2px 4px", fontSize: 11, borderRadius: 4 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Chat area ───────────────────────────────────── */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          {messages.length === 0 && !loading && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12, color: v("text-3") }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4 }}>
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
              </svg>
              <span style={{ fontSize: 13, textAlign: "center", lineHeight: 1.5, maxWidth: 280 }}>
                {t("chat.leer.text")}
              </span>
            </div>
          )}

          {messages.map((msg, i) => {
            const isUser = msg.role === "user";
            if (isUser) {
              return (
                <div key={i} className="cp-user-msg" style={{ alignSelf: "flex-end", maxWidth: "82%", display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
                  {msg.images?.map((img, j) => (
                    <img key={j} src={`data:${img.mime};base64,${img.base64}`} alt={img.name} className="cp-img-preview" />
                  ))}
                  {msg.content && (
                    <div style={{
                      padding: "8px 12px", borderRadius: 12, fontSize: 13, lineHeight: 1.6,
                      color: v("text-1"), background: v("surface-1"),
                      border: `1px solid ${v("border")}`,
                      whiteSpace: "pre-wrap", wordBreak: "break-word",
                    }}>
                      {msg.content}
                    </div>
                  )}
                </div>
              );
            }
            // Assistant message - rendered with marked
            const rawHtml = marked.parse(msg.content || "");
            const withCopy = rawHtml.replace(/<pre>/g, `<pre><button class="cp-copy-btn" data-action="copy-code">${t("chat.eingabe.kopieren")}</button>`);
            return (
              <div key={i} style={{ alignSelf: "flex-start", maxWidth: "88%", padding: "8px 12px", borderRadius: 12, background: v("surface-2"), color: v("text-1") }}>
                <div className="cp-md" dangerouslySetInnerHTML={{ __html: withCopy }} />
              </div>
            );
          })}

          {/* Agent-Status (Multi-Agent-Pipeline) */}
          {loading && agentStatus && (Object.keys(agentStatus.agents || {}).length > 0 || agentStatus.mode === "agent") && (
            <div style={{ alignSelf: "stretch", background: v("surface-2"), border: `1px solid ${v("border")}`, borderRadius: 10, padding: "10px 12px", fontSize: 12 }}>
              <div style={{ marginBottom: 6, color: v("text-1"), fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--accent)", animation: "dotPulse 1.2s ease-in-out infinite" }} />
                {agentStatus.mode === "agent" ? t("chat.pipeline.titelAgent") : t("chat.pipeline.titelFrage")}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {AGENT_KEYS.map(k => {
                  const a = agentStatus.agents?.[k];
                  if (!a) return null;
                  const dot = STATUS_DOT[a.status] || { color: v("text-3") };
                  return (
                    <div key={k} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: dot.color, flexShrink: 0 }} />
                      <span style={{ width: 92, color: v("text-1") }}>{t("chat.agent." + k)}</span>
                      <span style={{ flex: 1, color: v("text-2"), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.step || "…"}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {loading && !agentStatus && (
            <div style={{ alignSelf: "flex-start", padding: "8px 12px", borderRadius: 12, background: v("surface-2") }}>
              <Dots />
            </div>
          )}

          {/* Deploy-Gate Inline-Block */}
          {!loading && pipelineSummary && (
            <div style={{ alignSelf: "stretch", background: v("surface-2"), border: `1px solid ${v("success")}`, borderRadius: 10, padding: 12, fontSize: 13 }}>
              <div style={{ fontWeight: 600, color: v("success"), marginBottom: 6 }}>{t("chat.pipeline.gruen")}</div>
              <div style={{ marginBottom: 4, color: v("text-1") }}>iFlow: <strong>{pipelineSummary.flowName || pipelineSummary.flowId}</strong></div>
              <div style={{ marginBottom: 8, color: v("text-2"), fontSize: 12 }}>Validator: {pipelineSummary.validationStatus} · QA: {pipelineSummary.qaStatus}</div>
              <div style={{ display: "flex", gap: 8 }}>
                {/* Der Auftrag an die Agenten bleibt woertlich: er loest den Deploy aus. */}
                <button onClick={() => { setPipelineSummary(null); send(`Bitte deploye den iFlow ${pipelineSummary.flowName || pipelineSummary.flowId} jetzt.`); }}
                  style={{ background: "var(--accent)", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  {t("chat.pipeline.deployen")}
                </button>
                <button onClick={() => { setPipelineSummary(null); setMessages(prev => [...prev, { role: "assistant", content: t("chat.pipeline.abgebrochen") }]); }}
                  style={{ background: "transparent", color: v("text-2"), border: `1px solid ${v("border")}`, borderRadius: 8, padding: "7px 14px", fontSize: 12, cursor: "pointer" }}>
                  {t("chat.pipeline.spaeter")}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Attached images preview ─────────────────────── */}
        {attachedImages.length > 0 && (
          <div style={{ padding: "6px 16px", display: "flex", gap: 6, flexWrap: "wrap", borderTop: `1px solid ${v("border")}` }}>
            {attachedImages.map((img, i) => (
              <div key={i} style={{ position: "relative" }}>
                <img src={`data:${img.mime};base64,${img.base64}`} alt={img.name} style={{ height: 48, borderRadius: 6, border: `1px solid ${v("border")}` }} />
                <button onClick={() => removeImage(i)} style={{
                  position: "absolute", top: -4, right: -4, width: 16, height: 16, borderRadius: "50%",
                  background: v("danger"), border: "none", color: "#fff", fontSize: 10, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
                }}>x</button>
              </div>
            ))}
          </div>
        )}

        {/* ── Input area ──────────────────────────────────── */}
        <div style={{ borderTop: `1px solid ${v("border")}`, padding: "10px 12px", display: "flex", gap: 6, alignItems: "flex-end", flexShrink: 0 }}>
          <input type="file" ref={fileRef} accept="image/*" multiple onChange={handleImageUpload} style={{ display: "none" }} />
          <button onClick={() => fileRef.current?.click()} title={t("chat.eingabe.bild")}
            style={{ background: "none", border: "none", color: v("text-3"), cursor: "pointer", padding: "6px 4px", display: "flex", flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
            </svg>
          </button>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            onPaste={handlePaste}
            placeholder={t("chat.eingabe.platzhalter")}
            rows={1}
            style={{
              flex: 1, fontFamily: FONT, fontSize: 13, padding: "7px 12px", borderRadius: 10,
              border: `1px solid ${v("border")}`, background: v("surface-1"), color: v("text-1"),
              outline: "none", resize: "none", maxHeight: 120, overflowY: "auto", lineHeight: 1.5,
              minHeight: 34,
            }}
            onInput={e => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"; }}
          />
          <button onClick={send} disabled={(!input.trim() && !attachedImages.length) || loading}
            style={{
              fontFamily: FONT, fontSize: 13, fontWeight: 600, padding: "7px 14px", borderRadius: 10,
              border: "none", flexShrink: 0, transition: "background .15s, color .15s",
              background: (!input.trim() && !attachedImages.length) || loading ? v("surface-2") : "var(--accent)",
              color: (!input.trim() && !attachedImages.length) || loading ? v("text-3") : "#fff",
              cursor: (!input.trim() && !attachedImages.length) || loading ? "default" : "pointer",
            }}>
            {t("chat.eingabe.senden")}
          </button>
        </div>
      </div>
    </>
  );
});

export default ChatPanel;
